import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FileText,
  FolderOpen,
  Users,
  Cloud,
  Search,
  Shield,
  Zap,
  Target,
  TrendingUp,
  CheckCircle,
  Database,
  Lock,
  Globe,
  Smartphone,
  BarChart3,
  Clock,
  Archive,
  Download,
  Upload,
  Menu,
  X,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
// Layout simple sans authentification
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import CountUp from '../components/CountUp';
import Logo from '../components/Logo';
import '../styles/background-effects.css';

// Aurora Background Effect Component - Fixed Full Height avec couleurs du logo "G efficience globale"
const AuroraBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {/* Gradient de fond avec les couleurs teal du logo */}
    <div className="absolute inset-0 bg-gradient-to-br from-teal-light via-teal-dark to-teal-800 opacity-10 teal-breathe"></div>
    
    {/* Premier cercle Aurora - Teal clair avec animation float */}
    <div className="absolute -top-40 -left-40 w-80 h-80 bg-teal-light rounded-full mix-blend-multiply filter blur-3xl opacity-30 aurora-float"></div>
    
    {/* Deuxième cercle Aurora - Teal foncé avec animation pulse personnalisée */}
    <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-teal-dark rounded-full mix-blend-multiply filter blur-3xl opacity-30 aurora-pulse-teal animation-delay-1000"></div>
    
    {/* Troisième cercle Aurora - Teal 800 avec animation drift */}
    <div className="absolute top-40 right-40 w-80 h-80 bg-teal-800 rounded-full mix-blend-multiply filter blur-3xl opacity-30 aurora-drift animation-delay-2000"></div>
    
    {/* Cercle supplémentaire pour plus de dynamisme - Teal 600 avec animation combinée */}
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 aurora-float animation-delay-1500"></div>
    
    {/* Éléments blancs pour plus de luminosité */}
    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-15 white-glow animation-delay-500"></div>
    <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-12 teal-white-flow animation-delay-1200"></div>
    <div className="absolute top-3/4 left-3/4 w-56 h-56 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-18 white-glow animation-delay-1800"></div>
    
    {/* Éléments blancs supplémentaires pour plus de richesse */}
    <div className="absolute top-1/6 right-1/6 w-48 h-48 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-14 white-sparkle animation-delay-300"></div>
    <div className="absolute bottom-1/6 left-1/6 w-60 h-60 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-16 white-wave animation-delay-800"></div>
    <div className="absolute top-2/3 right-2/3 w-52 h-52 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-13 white-pulse-glow animation-delay-1400"></div>
    <div className="absolute bottom-2/3 left-2/3 w-44 h-44 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-17 white-float-gentle animation-delay-1600"></div>
    <div className="absolute top-1/2 right-1/3 w-68 h-68 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-11 white-sparkle animation-delay-2000"></div>
    <div className="absolute bottom-1/2 left-1/3 w-40 h-40 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-19 white-wave animation-delay-2200"></div>
    
    {/* Overlay de gradient teal avec blanc pour un effet plus subtil */}
    <div className="absolute inset-0 teal-gradient-overlay"></div>
  </div>
);

const DocumentManagementPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Aurora Background Effect */}
      <AuroraBackground />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              className="flex items-center"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/" className="flex items-center space-x-2">
                <Logo className="w-16 h-16" />
                <span className="text-xl font-bold text-gray-900">omnia-eg</span>
              </Link>
            </motion.div>
            
            <motion.div 
              className="hidden md:flex items-center space-x-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Link to="/" className="text-gray-600 hover:text-teal-dark transition-colors">
                Accueil
              </Link>
              <Link to="/document-management" className="text-teal-dark font-medium">
                Gestion Documentaire
              </Link>
              <Link to="/strategic-writing" className="text-gray-600 hover:text-teal-dark transition-colors">
                Rédaction Stratégique
              </Link>
              <Link to="/communication-creation" className="text-gray-600 hover:text-teal-dark transition-colors">
                Communication
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link to="/login">
                <Button variant="primary" className="hidden sm:inline-flex">
                  Se connecter
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-12 content-above-aurora relative z-10">

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center py-16"
          >
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-teal-light bg-opacity-20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FileText className="w-10 h-10 text-teal-dark" />
              </div>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Gestion Documentaire
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Transformez votre gestion documentaire avec l'intelligence artificielle. 
              Intégrations cloud natives, classement automatisé et recherche sémantique 
              pour une efficacité maximale.
            </p>
          </motion.div>

          {/* Statistiques */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6"
          >
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-teal-light bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-teal-dark" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                <CountUp to={1000000} duration={2} delay={0.2} separator="," />
              </p>
              <p className="text-sm text-gray-600">Documents Gérés</p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Cloud className="w-6 h-6 text-teal-dark" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                <CountUp to={7} duration={2} delay={0.4} />
              </p>
              <p className="text-sm text-gray-600">Intégrations Cloud</p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-teal-600 bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-teal-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                <CountUp to={95} duration={2} delay={0.6} />
              </p>
              <p className="text-sm text-gray-600">% de Précision IA</p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-teal-800 bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-teal-800" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                <CountUp to={80} duration={2} delay={0.8} />
              </p>
              <p className="text-sm text-gray-600">% de Temps Économisé</p>
            </Card>
          </motion.div>

          {/* Objectifs du Module */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Objectifs du Module
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Centralisation Intelligente</h3>
                    <p className="text-gray-600">Rassemblez tous vos documents dans un écosystème unifié avec classification automatique par l'IA.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-dark rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Recherche Sémantique</h3>
                    <p className="text-gray-600">Trouvez instantanément le bon document grâce à la compréhension contextuelle de l'IA.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Intégrations Cloud</h3>
                    <p className="text-gray-600">Connectez Google Drive, Dropbox, OneDrive et autres services cloud en toute sécurité.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Collaboration Avancée</h3>
                    <p className="text-gray-600">Partagez, commentez et collaborez sur vos documents avec votre équipe en temps réel.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-light to-teal-dark rounded-2xl p-8 text-white">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Database className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">1M+</p>
                    <p className="text-sm opacity-80">Documents</p>
                  </div>
                  <div className="text-center">
                    <Logo className="w-16 h-16 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">95%</p>
                    <p className="text-sm opacity-80">Précision IA</p>
                  </div>
                  <div className="text-center">
                    <Globe className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">7</p>
                    <p className="text-sm opacity-80">Clouds</p>
                  </div>
                  <div className="text-center">
                    <Users className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">25+</p>
                    <p className="text-sm opacity-80">Équipes</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Impact dans l'Organisation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Impact dans l'Organisation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-light bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-teal-dark" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Productivité</h3>
                <p className="text-gray-600">
                  Réduction de <strong>80%</strong> du temps de recherche documentaire. 
                  Vos équipes se concentrent sur l'essentiel.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-dark bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-teal-dark" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Sécurité</h3>
                <p className="text-gray-600">
                  Chiffrement de bout en bout et contrôle d'accès granulaire. 
                  Vos documents sont <strong>100% sécurisés</strong>.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Conformité</h3>
                <p className="text-gray-600">
                  Respect automatique des réglementations RGPD et sectorielles. 
                  <strong>Zéro risque</strong> de non-conformité.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Avantages Clés */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Avantages Clés
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-light bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-teal-dark" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Rapidité</h3>
                <p className="text-sm text-gray-600">Accès instantané à vos documents avec recherche IA</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-6 h-6 text-teal-dark" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Sécurité</h3>
                <p className="text-sm text-gray-600">Chiffrement et contrôle d'accès avancés</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-600 bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Mobilité</h3>
                <p className="text-sm text-gray-600">Accès depuis tous vos appareils, partout</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-800 bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-6 h-6 text-teal-800" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Analytics</h3>
                <p className="text-sm text-gray-600">Insights sur l'utilisation de vos documents</p>
              </Card>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="text-center py-12"
          >
            <div className="bg-gradient-to-r from-teal-dark to-teal-light rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Prêt à Révolutionner votre Gestion Documentaire ?
              </h2>
              <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                Découvrez comment notre module peut transformer votre organisation 
                et améliorer l'efficacité de vos équipes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login" className="bg-white text-teal-dark hover:bg-gray-100 hover:scale-105 hover:shadow-lg font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center">Accéder à la plateforme</Link>
              </div>
            </div>
          </motion.div>

        </div>
        </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <motion.div 
                className="flex items-center mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-teal-dark to-teal-light rounded-lg flex items-center justify-center mr-3">
                  <Logo className="w-10 h-10" />
                </div>
                <span className="text-xl font-bold">omnia-eg</span>
              </motion.div>
              <motion.p 
                className="text-gray-400 mb-6 max-w-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                La plateforme IA qui révolutionne la gestion documentaire et la rédaction stratégique pour les organisations modernes.
              </motion.p>
              <motion.div 
                className="flex space-x-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-10 h-10 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors cursor-pointer">
                  <Globe className="w-5 h-5 text-teal-light" />
                </div>
                <div className="w-10 h-10 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors cursor-pointer">
                  <Mail className="w-5 h-5 text-teal-light" />
                </div>
                <div className="w-10 h-10 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors cursor-pointer">
                  <Phone className="w-5 h-5 text-teal-light" />
              </div>
              </motion.div>
            </div>
            
            <div>
              <motion.h3 
                className="font-semibold mb-6 text-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                Solutions
              </motion.h3>
              <motion.ul 
                className="space-y-3 text-gray-400"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <li><Link to="/document-management" className="hover:text-white transition-colors text-left hover:translate-x-1 transform block">Gestion Documentaire</Link></li>
                <li><Link to="/strategic-writing" className="hover:text-white transition-colors text-left hover:translate-x-1 transform block">Rédaction Stratégique</Link></li>
                <li><Link to="/communication-creation" className="hover:text-white transition-colors text-left hover:translate-x-1 transform block">Communication & Création</Link></li>
              </motion.ul>
            </div>
            

            

                    </div>
          
          <motion.div 
            className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2026 OmnIA-EG. Tous droits réservés.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
                  </div>
                </motion.div>
                </div>
      </footer>
      </div>
  );
};

export default DocumentManagementPage;